/**
 * CRT composite. Runs at output resolution, writes to the default framebuffer.
 *
 * Two sampling regimes share this shader and must not be confused:
 *
 *  - The scene, the persistence trail and the bloom are sampled through the
 *    barrel warp with `NearestFilter` on the scene texture. Pixels stay chunky.
 *  - The aperture grille, the scanlines, the vignette, the grain and the glass
 *    glare are evaluated in continuous *output* space. A real shadow mask is
 *    finer than a source pixel; computing it at buffer resolution would give
 *    you three fat stripes per sprite instead of a phosphor structure.
 *
 * Order matters. Every light-forming term below happens in linear HDR, and
 * tonemapping comes after all of them. Tonemap earlier and the bloom skirt clips
 * to white, which is exactly the failure this whole stack exists to avoid. The
 * grille is the one thing that runs *after* the tonemap: it is not light in the
 * scene, it is the structure of the surface emitting it, and applying it to the
 * displayed value is what lets its luminance be pinned. See the note there.
 */

import { GLSL_COLOR } from './common';

export const CRT_COMPOSITE_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tScene;
uniform sampler2D tPersist;
uniform sampler2D tBloom;
uniform sampler2D tHalation;

uniform vec2 uOutputSize;
uniform vec2 uBufferSize;
uniform float uTime;

uniform float uPersistMix;
uniform float uBloomIntensity;
uniform float uHalationStrength;
uniform vec3 uHalationTint;

uniform vec2 uCurvature;
uniform float uOverscan;
uniform float uChromaticAberration;

uniform float uMaskPitch;
uniform float uMaskStrength;
uniform float uScanlineStrength;
uniform float uScanlineSharpness;
/** Smallest scanline period, in output pixels. Never let one go under Nyquist. */
uniform float uScanlineMinPeriod;
/** Fades the grille out when the output scale is too small to hold it. */
uniform float uMaskFade;
/** The same for the scanlines, but floored: they thin out, they never leave. */
uniform float uScanFade;

uniform float uVignette;
uniform float uVignettePower;
uniform float uVignetteDesat;
uniform float uEdgeBlur;
uniform float uEdgeBlurStart;
uniform float uEdgeBlurPx;
uniform float uNoise;
uniform float uGlare;

in vec2 vUv;
out vec4 fragColor;

${GLSL_COLOR}

#define TAU 6.28318530718

float saturate(float x) { return clamp(x, 0.0, 1.0); }

// Separable barrel distortion. Overscan pulls the sampled region inward first,
// so the warp can push outward without ever reaching past the buffer edge.
vec2 warp(vec2 uv) {
  vec2 c = (uv * 2.0 - 1.0) / uOverscan;
  float x2 = c.x * c.x;
  float y2 = c.y * c.y;
  c.x *= 1.0 + uCurvature.x * y2;
  c.y *= 1.0 + uCurvature.y * x2;
  return c * 0.5 + 0.5;
}

// Current frame or its own afterglow, whichever is brighter. The trail adds
// light; it never takes any away.
vec3 tube(vec2 uv) {
  vec3 cur = texture(tScene, uv).rgb;
  vec3 trail = texture(tPersist, uv).rgb * uPersistMix;
  return max(cur, trail);
}

// Four taps on a diagonal ring, which is the cheapest kernel that reads as a
// lens rather than as a box: a lens spreads a point into a disc, and a ring of
// four is a two-bit disc. Offsets are in output pixels, because the softness
// belongs to the glass and not to the source grid — a fixed number of buffer
// pixels would mean a 4K window got a blur four times wider than a phone.
vec3 defocus(vec2 uv, float amount) {
  vec2 d = vec2(uEdgeBlurPx * amount) / uOutputSize;
  vec3 s = tube(uv + d);
  s += tube(uv - d);
  s += tube(uv + vec2(d.x, -d.y));
  s += tube(uv - vec2(d.x, -d.y));
  return s * 0.25;
}

void main() {
  vec2 w = warp(vUv);

  // One radial coordinate feeds both lens terms, taken in warped space so the
  // defocus and the vignette bend with the barrel instead of sitting square on
  // the framebuffer behind it. Normalised to 1.0 at the corners, so 0.707 is the
  // middle of an edge.
  vec2 rc = w * 2.0 - 1.0;
  float radius = length(rc) * 0.70710678;

  // Outside the tube is bezel, not wrapped signal.
  vec2 soft = 1.5 / uOutputSize;
  vec2 edge = smoothstep(vec2(0.0), soft, w) * smoothstep(vec2(0.0), soft, 1.0 - w);
  float inside = edge.x * edge.y;

  // Per-channel horizontal split, zero at the tube centre and growing toward
  // the edges the way a misconverged gun actually behaves.
  float cx = vUv.x * 2.0 - 1.0;
  vec2 ca = vec2(uChromaticAberration * cx / uOutputSize.x, 0.0);

  vec3 col;
  col.r = tube(w + ca).r;
  col.g = tube(w).g;
  col.b = tube(w - ca).b;

  // Lomo field curvature. A fast lens holds one plane in focus across a disc and
  // loses it outside, so the centre keeps the nearest-sampled pixel edges the
  // rest of this project is built around and only the outer third softens.
  //
  // Two ramps off the same mask, not one: the tap radius grows with it and so
  // does the mix, so the onset is quartic rather than linear and there is no
  // ring where the blur switches on. The branch keeps the taps off the middle of
  // the frame, and the mask is radial, so a whole warp takes the same side of it
  // — but it should not be credited with a saving. The taps are under the noise
  // floor of tools/perfprobe.mjs with or without it; see CRT.edgeBlur.
  //
  // Only the tube is defocused. Bloom and halation are already several passes of
  // blur wide; running four more taps over them would cost the same and change
  // nothing you could see.
  float soften = uEdgeBlur * smoothstep(uEdgeBlurStart, 1.0, radius);
  if (soften > 0.002) col = mix(col, defocus(w, soften), soften);

  col += texture(tBloom, w).rgb * uBloomIntensity;
  col += texture(tHalation, w).rgb * uHalationTint * uHalationStrength;

  // Aperture grille: three phase-shifted cosines, mean-normalised so the mask
  // redistributes energy between channels instead of dimming the picture. It is
  // only built here; it is applied at the bottom, on the tonemapped picture.
  vec3 grille = (0.5 + 0.5 * cos(TAU * gl_FragCoord.x / uMaskPitch - vec3(0.0, 2.09439510239, 4.18879020479))) * 2.0;
  vec3 mask = mix(vec3(1.0), grille, uMaskStrength * uMaskFade);

  // Raised cosine per beam line. A hard step here reads as a venetian blind;
  // the cosine reads as a beam with a finite spot size.
  //
  // One line per buffer row is what a tube does, and it is right for as long as
  // a buffer row is wider than about three output pixels. Below that it is not
  // faint, it is unrepresentable, and the old code answered that by switching
  // the scanlines off with the same fade that dims the grille.
  //
  // tools/lensprobe.mjs, real fits, output pixels per buffer row: 1920x1080
  // gives 2.87, 1391x787 gives 2.09 — a hair over Nyquist — and 405x870 gives
  // 0.60. tools/scanprobe.mjs then isolates what reaches the glass, by
  // differencing the resting frame against itself with uScanlineStrength at
  // zero and the grain off, as rms over the lit crop against its own mean:
  //
  //              old      new
  //   1920x1080  0.1254   0.1255
  //   1391x787   0.0980   0.1081
  //   405x870    0.0079   0.0454
  //
  // The bottom row is the bug. A window that shape had no beam structure at all
  // — 0.008 is the residue of a term that is not running.
  //
  // Capping the line count at one per uScanlineMinPeriod output pixels takes the
  // aliasing out by construction instead, and what is left to do at a small fit
  // is thinning rather than deletion. 2.8 is not arbitrary: 1920x1080 sits at
  // 2.87, so the top row is unchanged to four decimal places and the frame every
  // reference in reports/ was scored against keeps exactly the scanline it was
  // scored with. The middle row gains little in amplitude and a lot in
  // character: at 2.09 the stripe beat against the pixel grid and chopped the
  // lattice's horizontal lines into dashes, and at 2.80 they are solid again.
  float lines = min(uBufferSize.y, uOutputSize.y / max(uScanlineMinPeriod, 1.0));
  float row = w.y * lines;
  float band = pow(0.5 - 0.5 * cos(TAU * fract(row)), uScanlineSharpness);
  col *= mix(1.0, mix(1.0 - uScanlineStrength, 1.0, band), uScanFade);

  // Lomo vignette: radial, in the same warped space as the defocus.
  //
  // The old form was a product of the four edge distances raised to a quarter,
  // which removes 6% at half radius and 27% at the middle of an edge, then all
  // of its depth in the last few percent of the frame. That is a rim, not a
  // lens, and it is why
  // asking for a heavier vignette by raising the strength alone made the border
  // darker without the picture ever looking like it had been shot through
  // anything. A power law on the radius puts the falloff where a fast lens puts
  // it: at the default 0.55 / 2.0 the corner loses 55% of its light, the middle
  // of an edge 27%, and half radius still 14%.
  float fall = pow(saturate(radius), uVignettePower);
  col *= 1.0 - uVignette * fall;

  // Cheap glass loses saturation along with light, so the corner goes grey
  // rather than merely dark. Luminance-preserving by construction — the blend
  // target carries the same Rec.709 luma as the source — so this costs the
  // black floor nothing and is not a second darkening in disguise.
  col = mix(col, vec3(luma(col)), saturate(uVignetteDesat * uVignette * fall));

  float n = hash12(gl_FragCoord.xy + uTime * 137.0) - 0.5;
  col *= 1.0 + n * uNoise * 0.5;
  col += n * uNoise * 0.5;

  col *= inside;

  // Reflection sits on the glass, so it uses unwarped screen space and is not
  // masked by the bezel. Flattened and offset toward the upper left.
  vec2 gp = (vUv - vec2(0.26, 0.78)) * vec2(0.55, 1.9);
  col += uGlare * exp(-dot(gp, gp) * 6.0) * vec3(0.62, 0.74, 1.0);

  vec3 shown = acesFilm(max(col, vec3(0.0)));

  // The grille goes on last, and it is held to constant luminance.
  //
  // It runs as vertical stripes, so it modulates along X only. A vertical grid
  // line sits at one fixed phase and comes out solid; a horizontal one crosses
  // every phase along its length and came out chopped into dashes. Same mask,
  // opposite read, and the dashes are the reason the resting frame looked like a
  // rendering fault rather than like a tube.
  //
  // Two things fed that ripple, and the older divide-by-mask-luma form only
  // answered one. It flattens the mask for grey and for grey alone; the lattice
  // is a saturated blue, so its blue channel still swung with the blue stripe
  // while the normaliser tracked green-weighted mask luma. Removing *that*
  // residue changed the measured beading by nothing, because the larger term was
  // downstream: acesFilm is hue-preserving, which means it curves the peak
  // channel, so a chroma swing at constant scene luminance still arrives as a
  // 1.8x brightness swing on screen. No pre-tonemap normalisation can reach it.
  //
  // Masking the tonemapped picture instead puts the correction where the number
  // that matters lives — the light the tube actually emits — and one division
  // pins it. The phosphor stripes survive as chroma at full strength and every
  // hue now takes the same depth, rather than blue taking four times what grey
  // takes. Measured on the resting frame, the horizontal lines' modulation at
  // the mask pitch falls from 0.138 to 0.018, under the 0.058 the scanlines
  // already put on a vertical line — which is the point. The scanline ripple has
  // always read as a tube. Beading only read as a bug because it was four times
  // deeper than the thing next to it.
  //
  // Re-measured since, on the same tree with only this block reverted, by
  // tools/beading.py: horizontal modulation at the mask pitch 0.159 -> 0.024,
  // against 0.09-0.15 on a vertical. So the luminance claim above holds; the
  // vertical reference is nearer 0.09 than 0.058, which does not change the
  // inequality. Note the number has to be taken on *background* lattice lines.
  // Ranking rows by mean brightness lands on the ochre corridor instead, and
  // there the figure means nothing.
  //
  // What the fix does not do is make a horizontal line look like a vertical one,
  // and the sentence above about chroma surviving at full strength is the reason
  // — it is the remaining defect, written down as a feature. Per channel, along
  // a horizontal line, the modulation at the mask pitch is 0.30 R / 0.09 G /
  // 0.28 B: luminance is pinned flat, and the hue swings by a third every three
  // pixels. A vertical line sits at one phase and is one colour end to end. At
  // 5x that alternation still reads as a bead chain, which is why a reviewer can
  // be right about the picture and this comment right about the number at the
  // same time. Removing it means less grille, not a different normalisation:
  // the swing scales with CRT.maskStrength, and since this block pins
  // luminance, lowering it is very nearly free on every luminance metric the
  // look is scored against. That is a look decision, so it is not taken here.
  vec3 tinted = shown * mask;
  tinted *= luma(shown) / max(luma(tinted), 1e-4);

  // A bright stripe can ask for more than the display has. Clipping it there
  // would take the luminance back off the hot cores that the whole bloom stack
  // exists to put on. Blending toward the unmasked colour instead costs only
  // saturation, and costs no light at all: both ends of the blend carry the same
  // luminance by construction, so any mixture of them does too.
  float hi = max(tinted.r, max(tinted.g, tinted.b));
  float sh = max(shown.r, max(shown.g, shown.b));
  if (hi > 1.0) tinted = mix(tinted, shown, (hi - 1.0) / max(hi - sh, 1e-4));

  fragColor = vec4(linearToSrgb(clamp(tinted, 0.0, 1.0)), 1.0);
}
`;
